import { t } from "../../i18n";
import type { Attachment, Provider } from "@lumen/shared";
import {
  ArrowDown,
  ArrowUp,
  Camera,
  ChevronsUp,
  ChevronDown,
  ChevronRight,
  Globe2,
  GripVertical,
  Image,
  LoaderCircle,
  RefreshCw,
  Save,
  UserRound,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { ModelAvailabilityToggle } from "./ModelAvailabilityToggle";
import { ModelBadge } from "./ModelBadge";
import { ModelIcon } from "./ModelIcon";
import { ModelStat } from "./ModelStat";
import { ModelTagEditor } from "./ModelTagEditor";
import { Toggle } from "../Toggle";
import {
  formatNumber,
  inferred,
  metadataFor,
  type RichModel,
} from "../../pages/models/ModelsPage.utils";

export function ModelEditor({
  model,
  provider,
  allTags,
  canWrite,
  moving,
  canMoveUp,
  canMoveDown,
  onSaved,
  onReset,
  onMoveTop,
  onMove,
  onDragStart,
  onDrop,
}: {
  model: RichModel;
  provider?: Provider;
  allTags: string[];
  canWrite: boolean;
  moving: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSaved: (model: RichModel) => void;
  onReset: (model: RichModel) => void;
  onMoveTop: () => void;
  onMove: (direction: -1 | 1) => void;
  onDragStart: () => void;
  onDrop: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(model);
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragEnabled, setDragEnabled] = useState(false);
  useEffect(() => setDraft(model), [model]);
  useEffect(() => {
    const query = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setDragEnabled(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const capabilities = inferred(draft);
  const metadata = metadataFor(draft);
  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const image = await api<Attachment>("/uploads", { method: "POST", body });
      setDraft({ ...draft, icon: image.url });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Upload failed"));
    } finally {
      setBusy(false);
    }
  };
  const patch = async (values: Partial<RichModel>) =>
    api<RichModel>(`/models/${model.id}`, {
      method: "PATCH",
      body: JSON.stringify(values),
    });
  const toggleAvailability = async (enabled: boolean) => {
    setToggleBusy(true);
    setError("");
    try {
      const updated = await patch({ enabled });
      setDraft((current) => ({ ...current, ...updated }));
      onSaved({ ...draft, ...updated, enabled });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Availability could not be changed",
      );
    } finally {
      setToggleBusy(false);
    }
  };
  const toggleUserAvailability = async (enabled: boolean) => {
    setToggleBusy(true);
    setError("");
    try {
      await api(`/models/${model.id}/user-settings`, {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      });
      setDraft((current) => ({ ...current, userEnabled: enabled }));
      onSaved({ ...draft, userEnabled: enabled });
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Personal availability could not be changed",
      );
    } finally {
      setToggleBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const updated = await patch({
        name: draft.name,
        description: draft.description,
        icon: draft.icon,
        tags: draft.tags,
        enabled: draft.enabled,
        toolsEnabled: draft.toolsEnabled ?? true,
        capabilities,
      });
      onSaved({ ...draft, ...updated });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Model could not be saved");
    } finally {
      setBusy(false);
    }
  };
  return (
    <article
      onKeyDown={(event) => {
        if (
          canWrite &&
          open &&
          event.key === "Enter" &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          void save();
        }
      }}
      onDragOver={(event) => {
        if (canWrite && dragEnabled) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      className={`panel overflow-hidden ${moving ? "opacity-60" : ""}`}
    >
      <div
        className={`flex items-center gap-1 p-2 sm:p-3 ${draft.userEnabled === false ? "opacity-50" : ""}`}
      >
        <button
          type="button"
          draggable={canWrite && dragEnabled && !moving}
          className={`button p-2 ${dragEnabled ? "cursor-grab active:cursor-grabbing" : ""}`}
          disabled={!canWrite || moving}
          aria-label={`Drag to reorder ${draft.name}`}
          title={
            dragEnabled
              ? t("Drag to reorder")
              : t("Drag reordering is unavailable on touch devices")
          }
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "move";
            onDragStart();
          }}
        >
          <GripVertical size={17} />
        </button>
        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            className="button p-1.5"
            disabled={!canWrite || moving || !canMoveUp}
            title={t("Move up")}
            aria-label={`Move ${draft.name} up`}
            onClick={() => onMove(-1)}
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            className="button p-1.5"
            disabled={!canWrite || moving || !canMoveDown}
            title={t("Move down")}
            aria-label={`Move ${draft.name} down`}
            onClick={() => onMove(1)}
          >
            <ArrowDown size={14} />
          </button>
          <button
            type="button"
            className="button p-1.5"
            disabled={!canWrite || moving || !canMoveUp}
            title={t("Move to top")}
            aria-label={`Move ${draft.name} to top`}
            onClick={onMoveTop}
          >
            <ChevronsUp size={14} />
          </button>
        </div>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl p-2 text-left hover:bg-stone-50 dark:hover:bg-white/3"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <ModelIcon model={draft} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold">{draft.name}</span>
            <span className="block truncate text-xs text-stone-400">
              {provider?.name ?? draft.providerName ?? "Provider"} ·{" "}
              {draft.remoteId}
            </span>
            <span className="mt-2 flex flex-wrap gap-1">
              {draft.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold dark:bg-white/5"
                >
                  {tag}
                </span>
              ))}
              {capabilities.vision && (
                <ModelBadge icon={<Camera size={10} />} label={t("Vision")} />
              )}
              {capabilities.imageGeneration && (
                <ModelBadge
                  icon={<Image size={10} />}
                  label={t("Image output")}
                />
              )}
              {capabilities.tools && (
                <ModelBadge
                  icon={<Wrench size={10} />}
                  label={
                    (draft.toolsEnabled ?? true)
                      ? t("Tools on")
                      : t("Tools off")
                  }
                />
              )}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 flex-col">
          {draft.scope !== "personal" && (
            <ModelAvailabilityToggle
              checked={draft.userEnabled !== false}
              label={`${draft.userEnabled === false ? t("Enable") : t("Disable")} ${draft.name} for me`}
              icon={<UserRound size={15} />}
              disabled={!draft.enabled}
              busy={toggleBusy}
              onChange={(value) => void toggleUserAvailability(value)}
            />
          )}{" "}
          {canWrite && draft.scope !== "personal" && (
            <ModelAvailabilityToggle
              checked={draft.enabled}
              label={`${draft.enabled ? t("Disable") : t("Enable")} ${draft.name} globally`}
              icon={<Globe2 size={15} />}
              busy={toggleBusy}
              onChange={(value) => void toggleAvailability(value)}
            />
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-stone-200 bg-stone-50/60 p-5 dark:border-white/10 dark:bg-black/10">
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className="label">{t("Display name")}</span>
              <input
                className="field"
                disabled={!canWrite}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              <span className="label">{t("Icon")}</span>
              <div className="flex gap-2">
                <input
                  className="field"
                  disabled={!canWrite}
                  placeholder={t("Emoji or image URL")}
                  value={draft.icon ?? ""}
                  onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                />
                <label
                  className={`button shrink-0 border border-stone-200 dark:border-white/10 ${!canWrite ? "pointer-events-none opacity-50" : ""}`}
                >
                  <Camera size={15} /> {t("Upload")}
                  <input
                    className="sr-only"
                    type="file"
                    accept="image/*"
                    onChange={(e) => void upload(e.target.files?.[0])}
                  />
                </label>
              </div>
            </label>
            <label className="md:col-span-2">
              <span className="label">{t("Description")}</span>
              <textarea
                className="field min-h-24"
                disabled={!canWrite}
                value={draft.description ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
              />
            </label>
            <label className="md:col-span-2">
              <span className="label">{t("Tags")}</span>
              <ModelTagEditor
                value={draft.tags}
                suggestions={allTags}
                disabled={!canWrite}
                onChange={(tags) => setDraft({ ...draft, tags })}
              />
            </label>
          </div>
          <div className="mt-5 grid gap-3 rounded-xl border border-stone-200 bg-white p-4 text-sm sm:grid-cols-2 lg:grid-cols-4 dark:border-white/10 dark:bg-white/3">
            <ModelStat
              label={t("Modality")}
              value={metadata.architecture?.modality}
            />
            <ModelStat
              label={t("Context")}
              value={formatNumber(
                metadata.context_length ??
                  metadata.top_provider?.context_length,
              )}
            />
            <ModelStat
              label={t("Max output")}
              value={formatNumber(metadata.top_provider?.max_completion_tokens)}
            />
            <ModelStat
              label={t("Knowledge cutoff")}
              value={metadata.knowledge_cutoff ?? "—"}
            />
            <ModelStat
              label={t("Inputs")}
              value={metadata.architecture?.input_modalities?.join(", ")}
            />
            <ModelStat
              label={t("Outputs")}
              value={metadata.architecture?.output_modalities?.join(", ")}
            />
            <ModelStat
              label={t("Tokenizer")}
              value={metadata.architecture?.tokenizer}
            />
            <ModelStat
              label={t("Parameters")}
              value={metadata.supported_parameters?.join(", ")}
            />
          </div>
          <details className="mt-4 rounded-xl border border-stone-200 bg-white dark:border-white/10 dark:bg-white/3">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
              {t("Complete provider metadata")}
            </summary>
            <pre className="max-h-80 overflow-auto border-t border-stone-200 p-4 text-xs leading-5 dark:border-white/10">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </details>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            {(["vision", "imageGeneration", "tools"] as const).map((key) => (
              <label key={key} className="flex items-center gap-2">
                <Toggle
                  disabled={!canWrite || capabilities[key]}
                  checked={capabilities[key]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      capabilities: {
                        ...draft.capabilities,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
                {key === "imageGeneration"
                  ? "Image generation capability"
                  : `${key[0].toUpperCase()}${key.slice(1)} capability`}
              </label>
            ))}
            {capabilities.tools && (
              <label className="flex items-center gap-2">
                <Toggle
                  disabled={!canWrite}
                  checked={draft.toolsEnabled ?? true}
                  onChange={(e) =>
                    setDraft({ ...draft, toolsEnabled: e.target.checked })
                  }
                />
                {t("Allow tools for this model")}
              </label>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-stone-200 pt-4 dark:border-white/10">
            {canWrite && (
              <button
                type="button"
                className="button text-amber-700 dark:text-amber-300"
                onClick={() => onReset(draft)}
              >
                <RefreshCw size={15} /> {t("Reset model config")}
              </button>
            )}{" "}
            {canWrite && (
              <button
                className="button-primary ml-auto"
                disabled={busy}
                onClick={() => void save()}
              >
                {busy ? (
                  <LoaderCircle className="animate-spin" size={15} />
                ) : (
                  <Save size={15} />
                )}{" "}
                {t("Save model")}
              </button>
            )}
          </div>
          {error && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-300">
              {error}
            </p>
          )}
        </div>
      )}
    </article>
  );
}
